import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Mic, Search } from 'react-bootstrap-icons'
import { MarkdownRenderer } from '../Markdown/MarkdownRenderer'
import { useWidgetContext } from '../../context/WidgetContext'

type RelatedItem = {
  title: string
  reason?: string
}

type SearchResultLink = {
  title?: string
  link?: string
  url?: string
  snippet?: string
  source?: string | null
}

type SearchImage = {
  imageUrl: string
  title?: string
  source?: string | null
  link?: string | null
}

type PlaceItem = {
  name: string
  description?: string
  url?: string
  image?: string
}

type ParsedSearchPayload = {
  summary: string
  related: RelatedItem[]
  sources: SearchResultLink[]
  images: SearchImage[]
  places: PlaceItem[]
}

const SSE_DELIMITER = '\n\n'

const collapseWhitespace = (value: unknown) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()

const normalizeSummary = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (!Array.isArray(value)) return ''

  return value
    .map((entry) => {
      if (typeof entry === 'string') return entry
      if (entry && typeof entry === 'object' && typeof (entry as any).text === 'string') {
        return (entry as any).text
      }
      return ''
    })
    .filter(Boolean)
    .join('\n\n')
}

const stripMarkdownImages = (value: string) =>
  String(value || '').replace(/!\[[^\]]*]\([^)]*\)/g, '').replace(/\n{3,}/g, '\n\n').trim()

const extractHeadingAndBody = (markdown: string): { heading: string; body: string } => {
  const raw = String(markdown || '')
  if (!raw.trim()) return { heading: '', body: '' }
  const lines = raw.split(/\r?\n/)
  const headingIndex = lines.findIndex((line) => /^\s*#\s+\S+/.test(line))
  if (headingIndex === -1) return { heading: '', body: raw }

  const heading = collapseWhitespace(lines[headingIndex].replace(/^\s*#\s+/, ''))
  const bodyLines = [...lines.slice(0, headingIndex), ...lines.slice(headingIndex + 1)]
  if (bodyLines.length > headingIndex && bodyLines[headingIndex]?.trim() === '') {
    bodyLines.splice(headingIndex, 1)
  }

  return {
    heading,
    body: bodyLines.join('\n').trim(),
  }
}

const normalizeRelated = (value: unknown): RelatedItem[] => {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()

  return value
    .map((entry) => {
      if (typeof entry === 'string') {
        const title = collapseWhitespace(entry)
        if (!title) return null
        return { title }
      }
      if (!entry || typeof entry !== 'object') return null
      const obj = entry as Record<string, unknown>
      const title = collapseWhitespace(obj.title || obj.question || obj.name || obj.text || '')
      const reason = collapseWhitespace(obj.reason || obj.description || '')
      if (!title) return null
      return { title, reason: reason || undefined }
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    .filter((entry) => {
      const key = entry.title.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(0, 6)
}

const normalizeLinks = (value: unknown): SearchResultLink[] => {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null
      const data = entry as Record<string, unknown>
      const title = collapseWhitespace(data.title || data.name || data.question || '')
      const url = collapseWhitespace(data.url || data.link || '')
      const snippet = collapseWhitespace(data.snippet || data.description || data.text || '')
      const source = collapseWhitespace(data.source || data.domain || '')
      if (!title && !url && !snippet) return null
      const dedupeKey = `${url}|${title}`.toLowerCase()
      if (seen.has(dedupeKey)) return null
      seen.add(dedupeKey)
      return {
        title,
        url,
        link: url,
        snippet,
        source: source || undefined,
      }
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    .slice(0, 10)
}

const normalizeImages = (value: unknown): SearchImage[] => {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null
      const obj = entry as Record<string, unknown>
      const imageUrl = collapseWhitespace(obj.imageUrl || obj.image_url || obj.url || obj.image || '')
      if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) return null
      if (seen.has(imageUrl)) return null
      seen.add(imageUrl)
      const title = collapseWhitespace(obj.title || obj.name || '')
      const source = collapseWhitespace(obj.source || '')
      const link = collapseWhitespace(obj.link || obj.url || '')
      return {
        imageUrl,
        title: title || undefined,
        source: source || undefined,
        link: link || undefined,
      }
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
}

const normalizePlaces = (value: unknown): PlaceItem[] => {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null
      const obj = entry as Record<string, unknown>
      const name = collapseWhitespace(obj.name || obj.title || '')
      if (!name) return null
      const key = name.toLowerCase()
      if (seen.has(key)) return null
      seen.add(key)
      const description = collapseWhitespace(obj.description || obj.summary || '')
      const url = collapseWhitespace(obj.url || obj.link || '')
      const image = collapseWhitespace(obj.image || obj.imageUrl || obj.image_url || '')
      return {
        name,
        description: description || undefined,
        url: url || undefined,
        image: image.startsWith('http://') || image.startsWith('https://') ? image : undefined,
      }
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    .slice(0, 6)
}

const parseJsonLike = (raw: unknown): any | null => {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!trimmed) return null

  try {
    return JSON.parse(trimmed)
  } catch {
    // continue
  }

  const fenced = trimmed.match(/```json\s*([\s\S]*?)```/i) || trimmed.match(/```\s*([\s\S]*?)```/)
  if (!fenced?.[1]) return null

  try {
    return JSON.parse(fenced[1].trim())
  } catch {
    return null
  }
}

const findPayloadNode = (value: any, depth = 0): any | null => {
  if (!value || typeof value !== 'object' || depth > 8) return null

  if (
    'summary' in value ||
    'structuredSummary' in value ||
    'related' in value ||
    'ragSources' in value ||
    'rag_sources' in value ||
    'ragImages' in value ||
    'rag_images' in value ||
    'locations' in value
  ) {
    return value
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findPayloadNode(item, depth + 1)
      if (found) return found
    }
    return null
  }

  for (const key of Object.keys(value)) {
    const found = findPayloadNode((value as any)[key], depth + 1)
    if (found) return found
  }

  return null
}

const parsePayloadFromRaw = (rawResponse: unknown): ParsedSearchPayload => {
  const parsed = parseJsonLike(rawResponse)
  const payload = findPayloadNode(parsed) || parsed || {}

  return {
    summary: normalizeSummary((payload as any)?.summary || (payload as any)?.structuredSummary || ''),
    related: normalizeRelated((payload as any)?.related || []),
    sources: normalizeLinks(
      (payload as any)?.ragSources ||
        (payload as any)?.rag_sources ||
        (payload as any)?.links ||
        (payload as any)?.results ||
        []
    ),
    images: normalizeImages((payload as any)?.ragImages || (payload as any)?.rag_images || []),
    places: normalizePlaces((payload as any)?.locations || []),
  }
}

const extractSummaryFieldLoosely = (rawInput: string): string => {
  const raw = String(rawInput || '')
  const idx = raw.indexOf('"summary"')
  if (idx === -1) return ''
  const colonIdx = raw.indexOf(':', idx + 9)
  if (colonIdx === -1) return ''

  let i = colonIdx + 1
  while (i < raw.length && /\s/.test(raw[i])) i++
  if (i >= raw.length) return ''

  const decodeEscaped = (value: string): string =>
    value
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\r')
      .replace(/\\t/g, '\t')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\')

  if (raw[i] === '"') {
    i++
    let out = ''
    let escaped = false
    for (; i < raw.length; i++) {
      const ch = raw[i]
      if (escaped) {
        out += `\\${ch}`
        escaped = false
        continue
      }
      if (ch === '\\') {
        escaped = true
        continue
      }
      if (ch === '"') break
      out += ch
    }
    return decodeEscaped(out).trim()
  }

  if (raw[i] === '[') {
    const slice = raw.slice(i)
    const parts: string[] = []
    let cursor = 0
    while (cursor < slice.length) {
      const textKey = slice.indexOf('"text"', cursor)
      if (textKey === -1) break
      const colon = slice.indexOf(':', textKey + 6)
      if (colon === -1) break
      let j = colon + 1
      while (j < slice.length && /\s/.test(slice[j])) j++
      if (slice[j] !== '"') {
        cursor = j + 1
        continue
      }
      j++
      let out = ''
      let escaped = false
      for (; j < slice.length; j++) {
        const ch = slice[j]
        if (escaped) {
          out += `\\${ch}`
          escaped = false
          continue
        }
        if (ch === '\\') {
          escaped = true
          continue
        }
        if (ch === '"') break
        out += ch
      }
      const decoded = decodeEscaped(out).trim()
      if (decoded) parts.push(decoded)
      cursor = j + 1
    }
    return parts.join('\n\n').trim()
  }

  return ''
}

const buildFallbackRelated = (query: string): RelatedItem[] => {
  const topic = collapseWhitespace(query)
  if (!topic) return []

  return [
    { title: `What are the top things to do in ${topic}?`, reason: 'Explore options' },
    { title: `What should I know before visiting ${topic}?`, reason: 'Plan ahead' },
    { title: `What are common mistakes to avoid in ${topic}?`, reason: 'Save time' },
    { title: `What are the best local recommendations in ${topic}?`, reason: 'Go deeper' },
  ]
}

const buildFallbackPlaces = (images: SearchImage[]): PlaceItem[] =>
  images
    .filter((item) => collapseWhitespace(item.title))
    .slice(0, 3)
    .map((item) => ({
      name: collapseWhitespace(item.title),
      description: 'Mentioned in search results',
      url: item.link || undefined,
      image: item.imageUrl,
    }))

const parseSseChunk = (chunk: string) => {
  const lines = String(chunk || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
  let event = 'message'
  const dataLines: string[] = []

  for (const line of lines) {
    if (!line || line.startsWith(':')) continue
    if (line.startsWith('event:')) {
      event = line.slice(6).trim()
      continue
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart())
    }
  }

  return { event, data: dataLines.join('\n') }
}

const parseProjectIdFromLocation = () => {
  if (typeof window === 'undefined') return ''
  const value = new URLSearchParams(window.location.search).get('project_id')
  return value && value.trim().length > 0 ? value.trim() : ''
}

const getStreamingSummaryText = (rawBuffer: string): string => {
  const raw = String(rawBuffer || '')
  if (!raw.trim()) return ''

  const delimiterIdx = raw.indexOf('<<<JSON>>>')
  if (delimiterIdx !== -1) {
    const markdownPart = raw.slice(0, delimiterIdx).trim()
    const jsonPart = raw.slice(delimiterIdx + '<<<JSON>>>'.length).trim()
    const parsed = parsePayloadFromRaw(jsonPart)
    if (parsed.summary) return parsed.summary
    const loose = extractSummaryFieldLoosely(jsonPart)
    if (loose) return loose
    return markdownPart
  }

  const parsed = parsePayloadFromRaw(raw)
  if (parsed.summary) return parsed.summary

  const loose = extractSummaryFieldLoosely(raw)
  if (loose) return loose

  const markerCandidates = ['<<<JSON>>>', '\n>>>', '\n```json', '\n{"summary"', '>>>{"summary"']
  for (const marker of markerCandidates) {
    const idx = raw.indexOf(marker)
    if (idx > 0) {
      return raw.slice(0, idx).trim()
    }
  }

  return raw
}

export const SearchPanel = () => {
  const { agent, config, consumeQueuedPrompt, queuedPromptVersion } = useWidgetContext()
  const [query, setQuery] = useState('')
  const [activeQuery, setActiveQuery] = useState('')
  const [heading, setHeading] = useState('')
  const [summary, setSummary] = useState('')
  const [results, setResults] = useState<SearchResultLink[]>([])
  const [related, setRelated] = useState<RelatedItem[]>([])
  const [images, setImages] = useState<SearchImage[]>([])
  const [places, setPlaces] = useState<PlaceItem[]>([])
  const [error, setError] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)

  const streamAbortRef = useRef<AbortController | null>(null)
  const streamRunRef = useRef(0)

  const applyParsedResult = useCallback(
    (rawValue: string, fallbackQuery: string, options?: { keepExistingMedia?: boolean }) => {
      const parsed = parsePayloadFromRaw(rawValue)
      const summaryCandidate = parsed.summary || getStreamingSummaryText(rawValue)
      const cleanSummary = stripMarkdownImages(summaryCandidate || '')
      const headingParts = extractHeadingAndBody(cleanSummary)
      const relatedList =
        parsed.related.length > 0 ? parsed.related : buildFallbackRelated(fallbackQuery)

      if (cleanSummary) {
        setHeading(headingParts.heading || fallbackQuery)
        setSummary(headingParts.body || cleanSummary)
      }

      if (!options?.keepExistingMedia) {
        if (parsed.sources.length > 0) setResults(parsed.sources)
        if (parsed.images.length > 0) setImages(parsed.images)
      } else if (parsed.sources.length > 0) {
        setResults((prev) => (prev.length > 0 ? prev : parsed.sources))
      }

      setRelated(relatedList)

      if (parsed.places.length > 0) {
        setPlaces(parsed.places)
      } else if (parsed.images.length > 0) {
        setPlaces(buildFallbackPlaces(parsed.images))
      }
    },
    []
  )

  const runSearch = useCallback(
    async (rawQuery: string) => {
      const nextQuery = rawQuery.trim()
      if (!nextQuery) return
      if (!agent.id) {
        setError('Agent is not ready.')
        return
      }

      streamAbortRef.current?.abort()
      const runId = streamRunRef.current + 1
      streamRunRef.current = runId

      setError('')
      setActiveQuery(nextQuery)
      setHeading(nextQuery)
      setSummary('')
      setResults([])
      setRelated([])
      setImages([])
      setPlaces([])
      setIsStreaming(true)

      const controller = new AbortController()
      streamAbortRef.current = controller

      let bufferedRaw = ''
      let finalResponse = ''
      let sseBuffer = ''

      try {
        const formData = new FormData()
        formData.set('query', nextQuery)
        formData.set('agent_id', String(agent.id))
        formData.set('is_stream', 'true')
        formData.set('use_serp', 'true')

        const projectId = parseProjectIdFromLocation()
        if (projectId) formData.set('project_id', projectId)

        const baseApiUrl =
          typeof (config as any)?.baseApiUrl === 'string' &&
          String((config as any).baseApiUrl).trim()
            ? String((config as any).baseApiUrl).trim().replace(/\/$/, '')
            : '/api'

        const response = await fetch(`${baseApiUrl}/agents/${agent.id}/search`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: {
            Accept: 'text/event-stream',
          },
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error(`Search stream failed (${response.status})`)
        }
        if (!response.body) {
          throw new Error('Search stream body is empty')
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let isDone = false

        const handleEvent = (event: string, rawData: string) => {
          if (runId !== streamRunRef.current) return
          if (!rawData) return
          if (rawData === '[DONE]') {
            isDone = true
            return
          }

          let data: any = null
          try {
            data = JSON.parse(rawData)
          } catch {
            data = null
          }

          if (event === 'error') {
            const message = collapseWhitespace(data?.message || 'Search stream failed.')
            setError(message || 'Search stream failed.')
            isDone = true
            return
          }

          const metaSources = normalizeLinks((data as any)?.ragSources || (data as any)?.rag_sources)
          const metaImages = normalizeImages((data as any)?.ragImages || (data as any)?.rag_images)
          if (metaSources.length > 0) setResults((prev) => (prev.length > 0 ? prev : metaSources))
          if (metaImages.length > 0) {
            setImages((prev) => (prev.length > 0 ? prev : metaImages))
            setPlaces((prev) => (prev.length > 0 ? prev : buildFallbackPlaces(metaImages)))
          }

          if (event === 'delta' && typeof data?.delta === 'string') {
            bufferedRaw += data.delta
            const liveSummary = stripMarkdownImages(getStreamingSummaryText(bufferedRaw))
            if (liveSummary) {
              const headingParts = extractHeadingAndBody(liveSummary)
              setHeading(headingParts.heading || nextQuery)
              setSummary(headingParts.body || liveSummary)
            }
            return
          }

          if (event === 'final' && typeof data?.response === 'string') {
            finalResponse = data.response
            applyParsedResult(finalResponse, nextQuery, { keepExistingMedia: true })
            return
          }

          if (event === 'done') {
            isDone = true
          }
        }

        while (!isDone) {
          const { done, value } = await reader.read()
          if (done) break

          sseBuffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n')
          let boundary = sseBuffer.indexOf(SSE_DELIMITER)
          while (boundary !== -1) {
            const chunk = sseBuffer.slice(0, boundary)
            sseBuffer = sseBuffer.slice(boundary + SSE_DELIMITER.length)
            if (chunk.trim()) {
              const { event, data } = parseSseChunk(chunk)
              handleEvent(event, data)
            }
            boundary = sseBuffer.indexOf(SSE_DELIMITER)
          }
        }

        if (sseBuffer.trim()) {
          const { event, data } = parseSseChunk(sseBuffer)
          handleEvent(event, data)
          sseBuffer = ''
        }

        if (!finalResponse) {
          const fallbackRaw = bufferedRaw.trim()
          if (fallbackRaw) {
            applyParsedResult(fallbackRaw, nextQuery, { keepExistingMedia: true })
          }
        }
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          setError(collapseWhitespace(err?.message || 'Search request failed.'))
        }
      } finally {
        if (runId === streamRunRef.current) {
          setIsStreaming(false)
          streamAbortRef.current = null
        }
      }
    },
    [agent.id, applyParsedResult, config]
  )

  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    const queuedPrompt = consumeQueuedPrompt()
    if (!queuedPrompt) return
    setQuery(queuedPrompt)
    void runSearch(queuedPrompt)
  }, [consumeQueuedPrompt, queuedPromptVersion, runSearch])

  const placeholder = useMemo(
    () => agent.searchPlaceholder || 'Search for anything...',
    [agent.searchPlaceholder]
  )

  const showResults = isStreaming || Boolean(summary) || results.length > 0
  const showSideRail = summary.trim().length > 0 && (results.length > 0 || places.length > 0)
  const imageStrip = images.slice(0, 3)

  return (
    <div className="osirus-search-panel osirus-search-panel-fullscreen">
      <form
        className="osirus-search-panel-controls"
        onSubmit={(event) => {
          event.preventDefault()
          void runSearch(query)
        }}
      >
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
        />
        <button type="button" className="icon-btn" aria-label="Voice input" title="Voice input">
          <Mic />
        </button>
        <button type="submit" className="icon-btn" aria-label="Search" title="Search">
          <Search />
        </button>
      </form>

      {error && <div className="osirus-search-panel-error">{error}</div>}

      {!showResults && !error && (
        <div className="osirus-search-panel-empty">
          <h3>Search Ready</h3>
          <p>Ask a question to get grounded answers with citations.</p>
        </div>
      )}

      {showResults && (
        <div className={`osirus-search-panel-body ${showSideRail ? 'has-side' : 'no-side'}`}>
          <section className="osirus-search-main">
            {imageStrip.length > 0 && (
              <div className="osirus-search-image-strip">
                {imageStrip.map((item, index) => (
                  <a
                    key={`${item.imageUrl}-${index}`}
                    className="osirus-search-image-thumb"
                    href={item.link || item.imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={item.imageUrl} alt={item.title || `Result image ${index + 1}`} />
                  </a>
                ))}
              </div>
            )}

            <h1>{heading || activeQuery || query || 'Search'}</h1>

            <div className="osirus-search-summary">
              {summary ? (
                <MarkdownRenderer rawText={summary} />
              ) : (
                <p className="osirus-search-loading-text">
                  {isStreaming ? 'Streaming response...' : 'Building search response...'}
                </p>
              )}
            </div>

            {related.length > 0 && (
              <div className="osirus-search-related">
                <h2>Related Questions</h2>
                <div className="osirus-search-related-list">
                  {related.map((item, index) => (
                    <button
                      key={`${item.title}-${index}`}
                      type="button"
                      className="osirus-search-related-item"
                      onClick={() => {
                        setQuery(item.title)
                        void runSearch(item.title)
                      }}
                    >
                      <span className="question">{item.title}</span>
                      {item.reason && <span className="reason">{item.reason}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          {showSideRail && (
            <aside className="osirus-search-side">
              {results.length > 0 && (
                <div className="osirus-search-side-block">
                <div className="osirus-search-source-list">
                  {results.map((item, index) => {
                    const href = item.url || item.link || ''
                    const title = item.title || href || `Result ${index + 1}`
                    return (
                      <a
                        key={`${title}-${index}`}
                        className="osirus-search-source-card"
                        href={href || undefined}
                        target={href ? '_blank' : undefined}
                        rel={href ? 'noopener noreferrer' : undefined}
                      >
                        <strong>{title}</strong>
                        {item.snippet && <p>{item.snippet}</p>}
                      </a>
                    )
                  })}
                </div>
                </div>
              )}

              {places.length > 0 && (
                <div className="osirus-search-side-block">
                <h3>Places Mentioned</h3>
                <div className="osirus-search-place-list">
                  {places.map((item, index) => (
                    <a
                      key={`${item.name}-${index}`}
                      className="osirus-search-place-card"
                      href={item.url || undefined}
                      target={item.url ? '_blank' : undefined}
                      rel={item.url ? 'noopener noreferrer' : undefined}
                    >
                      {item.image && <img src={item.image} alt={item.name} />}
                      <div className="place-copy">
                        <strong>{item.name}</strong>
                        {item.description && <p>{item.description}</p>}
                      </div>
                    </a>
                  ))}
                </div>
                </div>
              )}
            </aside>
          )}
        </div>
      )}
    </div>
  )
}
